/**
 * SiteList
 * Renders a scrollable list of sites with a collapsible email selection UI per site.
 * Each site panel provides:
 * - A parent checkbox to select/deselect all email addresses for that site
 * - Child checkboxes for individual email addresses via `Checkbox.Group`
 * The component maintains a parent-controlled state map of selected email IDs per site
 * and exposes actions to open messaging/email modals with the current selections.
 *
 * State & data:
 * - Builds `emailItemsData` (siteId -> EmailObjectId[]) from `tableData` via `useMemo`.
 * - Controls `selectedEmailItems` (siteId -> EmailObjectId[]) via `useState`.
 * - Initializes selections to "all" per site when data changes (`useEffect`).
 * - Uses stable handlers (`useCallback`) to update only affected site entries,
 *   minimizing child re-renders alongside `React.memo`-wrapped items.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.tableData Array of site objects; each must include `ObjectId`, `Name`, and `ClientList.EmailAddressList`.
 * @param {Function} props.openModal Function to open modals; used for message/email actions.
 * @returns {JSX.Element} Scrollable site list with per-site email selection controls.
 */

import Icon, { UserOutlined } from "@ant-design/icons";
import { Avatar, Card, Checkbox, Col, Collapse, Button as AntButton, Row, Space, Typography } from "antd";
import Button from "@ui/form-controls/button";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as PhoneCall } from "@assets/images/svg/phoneCall.svg";

import { CreateSitePageLink } from "utils/page-link-generator";
import { getColor } from "../../helper";

import CallModal from "@ui/candidate/call-modal";

const CheckboxGroup = Checkbox.Group;
const { Text } = Typography;
const { Panel } = Collapse;

function SiteList({ tableData, openModal }) {
  // Track selected emails per site in state (controlled by parent)
  const [selectedEmailItems, setSelectedEmailItems] = useState({});

  // Build map of siteId -> all available email addresses for that site
  const emailItemsData = useMemo(() => {
    const map = {};
    tableData?.forEach(site => {
      const emails =
        site?.ClientList?.EmailAddressList?.map(contact => contact.EmailObjectId) || [];
      if (emails.length) map[site.ObjectId] = emails;
    });
    return map;
  }, [tableData]);

  // Stable empty array reference to avoid prop changes
  const EMPTY = useMemo(() => [], []);

  // Update only the changed site's selection
  const onChangeChildCheckbox = useCallback((siteObjectId, checkedValues) => {
    setSelectedEmailItems(prev => {
      if (prev[siteObjectId] === checkedValues) return prev;
      return { ...prev, [siteObjectId]: checkedValues };
    });
  }, []);

  const onChangeParentCheckbox = useCallback(
    (e, siteObjectId) => {
      const next = e.target.checked ? emailItemsData[siteObjectId] || EMPTY : EMPTY;
      setSelectedEmailItems(prev => {
        if (prev[siteObjectId] === next) return prev;
        return { ...prev, [siteObjectId]: next };
      });
    },
    [emailItemsData, EMPTY]
  );

  const selectAllIdsHandler = e => {
    if (e.target.checked) {
      setSelectedEmailItems(emailItemsData);
    } else {
      setSelectedEmailItems({});
    }
  };

  // Initialize selections when data changes (default: all checked per site)
  useEffect(() => {
    setSelectedEmailItems(emailItemsData);
  }, [emailItemsData]);

  return (
    <>
      {/* send msg/email Buttons */}
      <Row justify="end">
        <Col>
          <Button
            variant="primary"
            onClick={() =>
              openModal({
                modalName: "SendEmailByTemplateToSiteModal",
                selectedEmailItems: selectedEmailItems
              })
            }
          >
            Send Email
          </Button>
        </Col>
      </Row>

      <Row align="middle" gutter={8}>
        <Col>
          <Checkbox
            indeterminate={
              Object.values(selectedEmailItems).flat().length !== 0 &&
              Object.values(selectedEmailItems).flat().length !==
                Object.values(emailItemsData).flat().length
            }
            checked={
              Object.values(selectedEmailItems).flat().length ===
              Object.values(emailItemsData).flat().length
            }
            onChange={e => selectAllIdsHandler(e)}
          />
        </Col>
        <Col>
          <Text className="font-bold" level={5}>
            Select All Sites Contacts ({`${Object.values(selectedEmailItems).flat().length}`})
          </Text>
        </Col>
      </Row>

      <Card className="site-list-card">
        {tableData?.map(site => (
          <SiteListItem
            key={site.ObjectId}
            site={site}
            siteId={site.ObjectId}
            checkedList={selectedEmailItems[site.ObjectId] || EMPTY}
            onChangeChildCheckbox={onChangeChildCheckbox}
            onChangeParentCheckbox={onChangeParentCheckbox}
          />
        ))}
      </Card>
    </>
  );
}

export default SiteList;

const SiteListItem = React.memo(function SiteListItem(props) {
  const { site, siteId, checkedList, onChangeChildCheckbox, onChangeParentCheckbox } = props;
  const plainOptions =
    site?.ClientList?.EmailAddressList?.map(contact => contact.EmailObjectId) || [];

  const checkAll = plainOptions.length > 0 && checkedList.length === plainOptions.length;
  const indeterminate = checkedList.length > 0 && checkedList.length < plainOptions.length;

  const onChildChange = list => {
    onChangeChildCheckbox(siteId, list);
  };

  const onParentChange = e => {
    onChangeParentCheckbox(e, siteId);
  };

  return (
    <CollapsableSite
      checkedList={checkedList}
      onCheckboxChange={onChildChange}
      contactDetails={site.ClientList}
      key={site.ObjectId}
      header={site.Name}
    >
      <Row justify="space-between" align="middle">
        <Col span={23}>
          <Space size="small">
            <Avatar
              style={{
                backgroundColor: "green",
                verticalAlign: "middle"
              }}
              size="small"
            >
              <UserOutlined />
            </Avatar>

            <Checkbox indeterminate={indeterminate} onChange={onParentChange} checked={checkAll} />

            <CreateSitePageLink
              mode="edit"
              ObjectId={site?.ObjectId}
              personObjectId={site?.ObjectId}
              Name={encodeURIComponent(site.Name)}
              target="_blank"
            >
              {site.Name}
            </CreateSitePageLink>

            {site?.Badges &&
              Array.isArray(site?.Badges) &&
              site?.Badges.map((data, idx) => (
                <AntButton
                  key={idx}
                  style={{ backgroundColor: getColor(data.BadgeType) }}
                  className="cursor-default"
                >
                  {data?.Text}
                </AntButton>
              ))}
          </Space>
        </Col>

        <Col>
          <CallModal personObjectId={site.ObjectId}>
            <Icon className="cursor-pointer text-xl" component={PhoneCall} />
          </CallModal>
        </Col>
      </Row>
    </CollapsableSite>
  );
});

/**
 * CollapsableSite - A collapsible component that displays site contact details
 *
 * This component renders an Ant Design Collapse panel containing email address
 * and phone number contact information for a site. Each contact entry is displayed
 * with a checkbox and formatted contact details.
 *
 * @param {Object} props - The component props
 * @param {Object} props.contactDetails - Contact information object containing email and phone lists
 * @param {Array} props.contactDetails.EmailAddressList - Array of email contact objects
 * @param {string} props.contactDetails.EmailAddressList[].EmailObjectId - Contact's email object ID
 * @param {string} props.contactDetails.EmailAddressList[].forename - Contact's first name
 * @param {string} props.contactDetails.EmailAddressList[].surname - Contact's last name
 * @param {string} props.contactDetails.EmailAddressList[].job_title - Contact's job title
 * @param {string} props.contactDetails.EmailAddressList[].contact_description - Contact's description
 * @param {Array} props.contactDetails.PhoneNumberList - Array of phone contact objects (currently commented out)
 * @param {string|number} props.key - Unique key identifier for the collapse panel
 * @param {React.ReactNode} props.children - React children elements to be rendered as the collapse header
 *
 * @returns {JSX.Element} A Collapse component with Panel containing formatted contact details
 *
 */
const CollapsableSite = ({ contactDetails, key, children, onCheckboxChange, checkedList }) => {
  const { EmailAddressList } = contactDetails;
  const emailItemsToRender = [
    "email_address",
    "forename",
    "surname",
    "job_title",
    "contact_description"
  ];

  const renderItem = (entryItem, keysToRender) => {
    return keysToRender
      .map(key => entryItem[key])
      .filter(Boolean)
      .join(" - ");
  };

  const onChange = checkedValues => {
    onCheckboxChange(checkedValues);
  };

  return (
    <Collapse
      collapsible="icon"
      className="collapsable-site pt-1 pb-1 bg-transparent rounded-none border-0 border-b border-gray"
    >
      <Panel header={children} key={key}>
        <CheckboxGroup value={checkedList} onChange={onChange}>
          {EmailAddressList.length === 0 ? (
            <Text className="table-td-color">No contacts available.</Text>
          ) : (
            EmailAddressList.map((item, idx) => {
              return (
                <div className="flex gap-2" key={key + "EmailEntry" + idx}>
                  <Checkbox value={item.EmailObjectId} />
                  <span className="table-td-color">{renderItem(item, emailItemsToRender)}</span>
                </div>
              );
            })
          )}
        </CheckboxGroup>
      </Panel>
    </Collapse>
  );
};
